import { useEffect, useRef } from 'react'
import { useTelegramAuth } from '@/hooks/useTelegramAuth'
import {
  useUserAuthSessionStore,
  useAppHydrationStore,
  useTelegramAuthStore,
} from '@/store'
import { useStartParamStore } from '@/store/useStartParamStore'
import { debugUtils } from '@/config/app'
import { router } from '@/routes'

/**
 * TelegramSessionInitializer
 *
 * Automatically initiates Telegram authentication on app start when:
 * 1. Running inside Telegram Mini App environment
 * 2. No existing session is active
 * 3. Hydration is complete
 *
 * Flow:
 * - Calls /telegram/initiate with initData from launch parameters
 * - If user is already linked: receives tokens, initializes session, navigates to dashboard
 * - If user is not linked: receives telegram_id, stores it, navigates to auth flow
 *
 * The telegram_id is stored in useTelegramAuthStore for later use when linking
 * after the user logs in via email/phone.
 *
 * Note: Uses router.navigate() directly since this component is rendered
 * outside of RouterProvider context.
 */
export const TelegramSessionInitializer = () => {
  const hasInitiated = useRef(false)

  const { isInTelegram, initiateTelegramAuth } = useTelegramAuth()

  const isSessionOpen = useUserAuthSessionStore((state) => state.isSessionOpen)
  const token = useUserAuthSessionStore((state) => state.token)
  const isAuthSessionRestored = useAppHydrationStore(
    (state) => state.isAuthSessionRestored,
  )
  const telegramAuthStatus = useTelegramAuthStore((state) => state.status)

  useEffect(() => {
    const initTelegramAuth = async () => {
      // Prevent multiple initiations
      if (hasInitiated.current) {
        debugUtils.authLog('[TelegramSessionInitializer] Already initiated, skipping')
        return
      }

      // Wait for hydration to complete
      if (!isAuthSessionRestored) {
        debugUtils.authLog('[TelegramSessionInitializer] Waiting for auth session restore...')
        return
      }

      // Only run in Telegram environment
      if (!isInTelegram) {
        debugUtils.authLog('[TelegramSessionInitializer] Not in Telegram, skipping')
        return
      }

      // Skip if already have a session
      if (isSessionOpen && token) {
        debugUtils.authLog('[TelegramSessionInitializer] Session already open, skipping')
        return
      }

      // Skip if already checking or authenticated
      if (telegramAuthStatus === 'checking' || telegramAuthStatus === 'authenticated') {
        debugUtils.authLog('[TelegramSessionInitializer] Already in progress or authenticated')
        return
      }

      debugUtils.authLog('[TelegramSessionInitializer] Starting Telegram auth initiation...')
      hasInitiated.current = true

      try {
        const result = await initiateTelegramAuth()

        if (!result) {
          // Allow retry on failure
          hasInitiated.current = false
          return
        }

        if (result.access_token && result.refresh_token) {
          // User is linked - tokens already saved by initiateTelegramAuth
          // Navigate to pending startparam route or dashboard
          // Small delay to ensure router is ready
          setTimeout(() => {
            const pendingRoute = useStartParamStore.getState().getPendingRoute()
            const destination = pendingRoute || '/dashboard'
            router.navigate(destination, { replace: true })
            if (pendingRoute) {
              useStartParamStore.getState().markConsumed()
            }
          }, 100)
        } else {
          // User not linked - telegram_id is stored in useTelegramAuthStore
          // No navigation needed - user will see the main page (/) and can start auth flow
          // The telegram_id is stored and linking will happen automatically after login
        }
      } catch (error) {
        debugUtils.authLog('[TelegramSessionInitializer] Error during initiation:', error)
        // On error, allow retry on next render cycle
        hasInitiated.current = false
      }
    }

    initTelegramAuth()
  }, [
    isInTelegram,
    isSessionOpen,
    token,
    isAuthSessionRestored,
    telegramAuthStatus,
    initiateTelegramAuth,
  ])

  // This component doesn't render anything
  return null
}
